import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Shield, Lock, CheckCircle, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface AdminRecord {
  id: string;
  score: number;
  created_at: string;
  player_email?: string;
  session_id?: string;
}

interface AdminCoupon {
  id: string;
  code: string;
  discount_percent: number;
  status: string;
  issued_at: string;
  expires_at?: string;
  email_sent_at?: string;
  internal_notified_at?: string;
  linked_high_score_id?: string;
  high_scores?: { score: number; player_email?: string } | null;
}

const Admin = () => {
  const [adminKey, setAdminKey] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highScores, setHighScores] = useState<AdminRecord[]>([]);
  const [coupons, setCoupons] = useState<AdminCoupon[]>([]);
  const [error, setError] = useState('');
  const [redeemingId, setRedeemingId] = useState<string | null>(null);
  const navigate = useNavigate();

  const logout = () => {
    setAuthenticated(false);
    setAdminKey('');
    setHighScores([]);
    setCoupons([]);
    navigate('/');
  };

  const authenticate = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase.functions.invoke('admin-data', {
        headers: { 'x-admin-key': adminKey },
      });
      if (err) throw err;
      if (data.error) { setError(data.error); return; }
      setHighScores(data.highScores || []);
      setCoupons(data.coupons || []);
      setAuthenticated(true);
    } catch (e: any) {
      setError('Virheellinen avain tai yhteysvirhe');
    } finally {
      setLoading(false);
    }
  };

  const redeemCoupon = async (couponId: string) => {
    if (!confirm('Merkitäänkö kuponki käytetyksi?')) return;
    setRedeemingId(couponId);
    try {
      const { data, error: err } = await supabase.functions.invoke('redeem-coupon', {
        headers: { 'x-admin-key': adminKey },
        body: { couponId },
      });
      if (err) throw err;
      setCoupons(prev => prev.map(c => c.id === couponId ? { ...c, status: 'redeemed' } : c));
    } catch (e) {
      console.error('Redeem failed:', e);
    }
    setRedeemingId(null);
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="glass-card p-8 max-w-sm w-full text-center">
          <Lock className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-xl font-bold text-foreground mb-4">Admin</h1>
          <input
            type="password"
            value={adminKey}
            onChange={e => setAdminKey(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && authenticate()}
            placeholder="Admin-avain"
            className="w-full rounded-xl bg-secondary px-4 py-3 text-sm border border-border focus:outline-none focus:ring-2 focus:ring-accent mb-3"
          />
          {error && <p className="text-destructive text-sm mb-3">{error}</p>}
          <button onClick={authenticate} disabled={loading} className="btn-gradient w-full">
            {loading ? 'Ladataan...' : 'Kirjaudu'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Title */}
        <div className="text-center py-4 md:py-6 mb-6 relative">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold gradient-text tracking-tight flex items-center justify-center gap-3">
            <Shield className="w-8 h-8 md:w-10 md:h-10 text-accent" />
            Haiko-Tetris Admin
          </h1>
          <button
            onClick={logout}
            className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-2 px-4 py-2 rounded-xl bg-secondary text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors text-sm font-medium"
          >
            <LogOut className="w-4 h-4" />
            Kirjaudu ulos
          </button>
        </div>

        {/* High Scores */}
        <div className="glass-card p-4 mb-6 overflow-x-auto">
          <h2 className="font-semibold text-foreground mb-3">Ennätykset ({highScores.length})</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="pb-2 pr-4">Pisteet</th>
                <th className="pb-2 pr-4">Aika</th>
                <th className="pb-2 pr-4">Sähköposti</th>
                <th className="pb-2">Session</th>
              </tr>
            </thead>
            <tbody>
              {highScores.map(hs => (
                <tr key={hs.id} className="border-b border-border/50">
                  <td className="py-2 pr-4 font-bold text-foreground">{hs.score}</td>
                  <td className="py-2 pr-4 text-muted-foreground">{new Date(hs.created_at).toLocaleString('fi-FI')}</td>
                  <td className="py-2 pr-4 text-muted-foreground">{hs.player_email || '—'}</td>
                  <td className="py-2 text-muted-foreground text-xs font-mono">{hs.session_id?.slice(0, 8) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Coupons */}
        <div className="glass-card p-4 overflow-x-auto">
          <h2 className="font-semibold text-foreground mb-3">Kupongit ({coupons.length})</h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="pb-2 pr-4">Koodi</th>
                <th className="pb-2 pr-4">Ennätys</th>
                <th className="pb-2 pr-4">Status</th>
                <th className="pb-2 pr-4">Myönnetty</th>
                <th className="pb-2 pr-4">Vanhenee</th>
                <th className="pb-2 pr-4">Email</th>
                <th className="pb-2">Toiminto</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map(c => (
                <tr key={c.id} className="border-b border-border/50">
                  <td className="py-2 pr-4 font-mono font-bold text-accent">{c.code}</td>
                  <td className="py-2 pr-4 text-muted-foreground">
                    {c.high_scores ? (
                      <span>{c.high_scores.score} pts{c.high_scores.player_email ? ` · ${c.high_scores.player_email}` : ''}</span>
                    ) : '—'}
                  </td>
                  <td className="py-2 pr-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      c.status === 'issued' ? 'bg-accent/20 text-accent' :
                      c.status === 'redeemed' ? 'bg-primary/20 text-primary' :
                      'bg-destructive/20 text-destructive'
                    }`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-muted-foreground">{new Date(c.issued_at).toLocaleString('fi-FI')}</td>
                  <td className="py-2 pr-4 text-muted-foreground">{c.expires_at ? new Date(c.expires_at).toLocaleDateString('fi-FI') : '—'}</td>
                  <td className="py-2 pr-4 text-muted-foreground">{c.email_sent_at ? '✅' : '—'}</td>
                  <td className="py-2">
                    {c.status === 'issued' ? (
                      <button
                        onClick={() => redeemCoupon(c.id)}
                        disabled={redeemingId === c.id}
                        className="flex items-center gap-1 px-3 py-1 rounded-lg bg-accent/20 text-accent text-xs font-semibold hover:bg-accent/30 transition-colors disabled:opacity-50"
                      >
                        <CheckCircle className="w-3 h-3" />
                        {redeemingId === c.id ? '...' : 'Käytetty'}
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Admin;
